import { pxToRem } from "$global-providers/mui-provider/pixel-rem-calculators";
import {
  CircularProgress,
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  styled,
  type Theme,
} from "@material-ui/core";
import type {
  Palette,
  PaletteColor,
} from "@material-ui/core/styles/createPalette";

/**
 * Palette keys whose value is a full `PaletteColor` (i.e. have
 * `main`/`light`/`contrastText`). Structural keys like `grey`, `text` and
 * `background` are excluded because `getColorStyles` reads `.main` etc. off the
 * colour and they don't fit that shape.
 */
type PaletteColorName = {
  [K in keyof Palette]: Palette[K] extends PaletteColor ? K : never;
}[keyof Palette];

type WithCustomColor<T> = T & {
  "data-color"?: PaletteColorName;
  "data-circular"?: boolean;
};

/**
 * This functionality can be dropped after the introduction of MUI v5, as it
 * allows you to pass custom colours from your theme directly to the MUI
 * components.
 */
export const getColorStyles = (
  theme: Theme,
  {
    "data-color": dataColor = "primary",
    variant,
    disabled,
  }: WithCustomColor<ButtonProps>,
) => {
  if (disabled) {
    return {};
  }

  const paletteColor = theme.palette[dataColor];
  if (variant === "text") {
    return {
      color: paletteColor.main,
      backgroundColor: "transparent",
    };
  }

  if (variant === "outlined") {
    return {
      color: paletteColor.main,
      backgroundColor: "transparent",
      border: `1px solid ${theme.palette.grey[500]}`,
      "&:hover": {
        backgroundColor: theme.palette.grey[100],
      },
    };
  }

  return {
    color: paletteColor.contrastText,
    backgroundColor: paletteColor.main,
    "&:hover": {
      backgroundColor: paletteColor.light,
    },
  };
};

export interface ButtonProps extends Omit<MuiButtonProps, "color"> {
  loading?: boolean;
  color?: PaletteColorName;
  /** Icon-only round button (no text). */
  circular?: boolean;
}

const StyledButton = styled(MuiButton)<Theme, WithCustomColor<ButtonProps>>(
  ({ theme, ...props }) => {
    const circular = props["data-circular"];
    return {
      "&.MuiButton-root": {
        minWidth: "auto",
        padding: `${pxToRem(12)} ${pxToRem(24)}`,
        fontSize: pxToRem(16),
        whiteSpace: "nowrap",
        // outlined's 1px border sits inside the box, so trim 1px of side
        // padding to keep its label and width aligned with the other variants.
        ...(props.variant === "outlined" && { paddingInline: pxToRem(23) }),
        ...(props.variant === "text" && { paddingInline: 0 }),
        ...(circular && {
          width: pxToRem(48),
          height: pxToRem(48),
          minWidth: "unset",
          padding: pxToRem(12),
          borderRadius: "50%",
        }),
        ...getColorStyles(theme, props),
      },
      // text has no side padding, so cancel MUI's negative outer icon margins
      // (they assume padding) that would otherwise sit past the edge.
      "&.MuiButton-text .MuiButton-startIcon": { marginLeft: 0 },
      "&.MuiButton-text .MuiButton-endIcon": { marginRight: 0 },
    };
  },
);

const Children = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  '&[data-loading="true"]': {
    visibility: "hidden",
  },
});

const Loading = styled(CircularProgress)({
  position: "absolute",
});

/**
 * The standard app button — the single canonical button for the app. `color`
 * accepts any palette colour — a key whose value is a `PaletteColor`
 * (`primary`, `secondary`, `error`, `brand`, …); `loading` swaps the label for
 * a spinner. Pick the look with the official, typed MUI `variant`:
 *
 * | Look        | Props                                   |
 * | ----------- | --------------------------------------- |
 * | Filled      | `color="primary"   variant="contained"` |
 * | Filled alt  | `color="secondary" variant="contained"` |
 * | Outlined    | `color="primary"   variant="outlined"`  |
 * | Text        | `color="primary"   variant="text"`      |
 */
export const Button: FC<ButtonProps> = ({
  children,
  color = "primary",
  loading,
  circular,
  ...props
}) => (
  <StyledButton
    {...props}
    /*
     * We don't actually pass the color prop to the MuiButton as it logs nasty
     * errors. Instead we pass it as a custom prop to be used in the styles.
     *
     * In MUI v5 being able to use the Theme's palette directly for all
     * color props is supported.
     */
    data-color={color}
    data-circular={circular}
    disabled={props.disabled || loading}
  >
    <Children data-loading={loading}>{children}</Children>
    {loading && <Loading color="inherit" size={22} thickness={5} />}
  </StyledButton>
);
